import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import type {CarouselItemData} from '@Components/carousel/carousel.types';
import {addGrid, getGridItemForStoredData, getSelectedGridItems, toggleGridItemSelection, updateMaxSelectionNumberForGrid} from '@Components/base-grid';
import {getUniqueString} from '@Utils/string.util';
import {AnimatePresence, motion} from 'framer-motion';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import styles from './carousel.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';

export interface CarouselProps {
  items: Array<CarouselItemData>;
  onNext?: () => void;
  onPrev?: () => void;
  height?: number;
  selectionGroupId?: string;
  maxSelectionNumber?: number;
  onSingleItemDoubleClick?: (items: Array<GridItemStorage>) => Promise<void>;
  onCarouselItemClicked?: (carouselItemID: string) => void;
  nextBtnRightPositioning?: number;
  prevBtnLeftPositioning?: number;
  gridID?: string;
}

const GAP = 12;

const HEIGHT = 100;

const PADDING_TOP = 16;

function Carousel({items, onNext = (): void => {}, onPrev = (): void => {}, height = HEIGHT, ...props}: CarouselProps): ReactElement {
  const carouselItemContainerRef = useRef<HTMLUListElement>(null);
  const gridID = useRef(props.gridID ?? getUniqueString());
  const selectedItems = useAppSelector((state) => {
    return getSelectedGridItems(state, props.selectionGroupId ?? gridID.current);
  });
  const [index, setIndex] = useState(0);
  const movedRef = useRef(0);
  const dispatch = useAppDispatch();

  const getControlButton = (className: string, iconClass: string, callback: () => void): ReactElement => {
    return (
      <Icon
        className={className}
        isDisabled={false}
        icon={iconClass}
        type={IconType.SHADOW}
        onClick={callback}
        size={IconSize.SIZE_ICON_20}
        shape={IconShape.CIRCLE}
        isUsedAsButton
      />
    );
  };

  const showSlide = (width: number): void => {
    if (carouselItemContainerRef.current) {
      carouselItemContainerRef.current.scrollTo({
        left: width,
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const onPrevSlide = (): void => {
    let aggregate = 0;
    let cachedIndex = index - 1;
    for (let i = index; i >= 0; i--) {
      const width = (items[i].width / items[i].height) * height;
      if (carouselItemContainerRef.current && aggregate + width > carouselItemContainerRef.current.offsetWidth) {
        cachedIndex = i;
        break;
      }
      aggregate += width + GAP;
    }

    movedRef.current -= aggregate;
    showSlide(movedRef.current);
    setIndex(cachedIndex);
    onPrev();
  };

  const onNextSlide = (): void => {
    let aggregate = 0;
    let cachedIndex = index;
    for (let i = index; i < items.length; i++) {
      const width = (items[i].width / items[i].height) * height;
      if (carouselItemContainerRef.current && aggregate + width > carouselItemContainerRef.current.offsetWidth) {
        cachedIndex = i;
        break;
      }
      aggregate += width + GAP;
    }

    movedRef.current += aggregate;
    showSlide(movedRef.current);
    setIndex(cachedIndex);
    onNext();
  };

  const getGridItemForData = (item: CarouselItemData): ReactElement | null => {
    return getGridItemForStoredData({
      item: {...item.gridItemData, gridItemHeight: height},
      paddedThumb: false,
      isSmallItem: true,
      gridId: gridID.current,
      gridSelectionGroupId: props.selectionGroupId ?? gridID.current,
      className: `flex-center radius-4 ${styles.carouselItem} ${item.gridItemData.className ?? ''}`,
      showSelectionNumber: true,
      showHoverOverlay: true,
      onClicked: (e: React.MouseEvent, isDoubleClick: boolean): void => {
        if (isDoubleClick && props.onSingleItemDoubleClick && selectedItems.length === 1) {
          void props.onSingleItemDoubleClick(selectedItems);
        } else {
          dispatch(
            toggleGridItemSelection({
              gridItemId: item.gridItemData.id,
              gridSelectionGroupId: props.selectionGroupId ?? gridID.current,
            })
          );
        }
        if (props.onCarouselItemClicked) {
          props.onCarouselItemClicked(item.gridItemData.id);
        }
      },

      style: {width: `${(item.width * height) / item.height}px`, height: `${height}px`},
    });
  };

  const onCarouselContainerScroll = (e: React.UIEvent<HTMLElement>): void => {
    let scrollVal = e.currentTarget.scrollLeft;
    movedRef.current = scrollVal;

    let newIndex = 0;
    for (let i = 0; i < items.length; i++) {
      const width = (items[i].width / items[i].height) * height;
      if (scrollVal < width) {
        break;
      }

      scrollVal -= width + GAP;
      newIndex += 1;
    }

    setIndex(newIndex);
  };

  const shouldShowNextBtn = (): boolean => {
    if (carouselItemContainerRef && carouselItemContainerRef.current) {
      let cachedIndex = items.length - 1;
      let totalWidth = 0;
      for (let i = cachedIndex; i >= 0; i--) {
        if (carouselItemContainerRef.current.offsetWidth <= totalWidth) {
          break;
        }
        const itemWidth = (items[i].width / items[i].height) * height;
        totalWidth += GAP + itemWidth;
        cachedIndex -= 1;
      }
      return index - cachedIndex < 1;
    }

    return true;
  };

  const shouldShowPrevBtn = (): boolean => {
    return index !== 0;
  };

  useEffect(() => {
    dispatch(
      updateMaxSelectionNumberForGrid({
        gridSelectionGroupId: props.selectionGroupId ?? gridID.current,
        maxSelectionNumber: props.maxSelectionNumber ?? 0,
      })
    );
  }, [props.maxSelectionNumber]);

  useEffect(() => {
    dispatch(
      addGrid({
        id: gridID.current,
        gridSelectionGroupId: props.selectionGroupId ?? gridID.current,
        items: items.map((item) => {
          return item.gridItemData;
        }),
      })
    );
  }, []);

  return (
    <div className={`${styles.carouselContainer} flex-row-justify-center`} style={{height: `${height + PADDING_TOP}px`}}>
      <div className={`flex ${styles.carouselItemContainerWrapper} _full-width`}>
        <ul
          onScroll={onCarouselContainerScroll}
          ref={carouselItemContainerRef}
          className={`${styles.carouselItemContainer} spacing-m-0 _full-width _full-height`}
          style={{gap: `${GAP}px`, paddingTop: `${PADDING_TOP}px`}}
        >
          {items.map(getGridItemForData)}
        </ul>
      </div>

      <AnimatePresence initial={false}>
        {shouldShowPrevBtn() ? (
          <motion.div
            className={`${styles.carouselControls} ${styles.pushToLeft}`}
            style={{top: `${PADDING_TOP / 2}px`, left: props.prevBtnLeftPositioning ? `${props.prevBtnLeftPositioning}px` : undefined}}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.2}}
            exit={{opacity: 0}}
          >
            {getControlButton(styles.prevBtn, 'icon-caret-left', onPrevSlide)}
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {shouldShowNextBtn() ? (
          <motion.div
            className={`${styles.carouselControls} ${styles.pushToRight}`}
            style={{top: `${PADDING_TOP / 2}px`, right: props.nextBtnRightPositioning ? `${props.nextBtnRightPositioning}px` : undefined}}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.2}}
            exit={{opacity: 0}}
          >
            {getControlButton(styles.nextBtn, 'icon-caret-right', onNextSlide)}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default Carousel;
